import { AxiosError, AxiosInstance } from "axios";
import { api } from '../../../../api/axiosInstance';
import { CreateSlotDTO, Slot, SlotSummary, SlotSearchParams, GetSlotsResponse, GetPaginationInfoResponse, SlotRankHistoryMap, BatchUpdateResponse, CheckExistsResponse, BatchDetailResponse, BatchSlotSideEffectList } from "../../../Slot/Model/Slot";
import { SlotRepository } from "../../../Slot/Domain/Repositories/SlotRepository";
import { User } from "../../../User/Model/User";
import { GetAllSlotsResponse } from "../../../Slot/Domain/UseCase/GetAllSlotsUseCase";

function firstErrorMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    const message = value.trim();
    return message || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    const data = value as Record<string, unknown>;
    for (const key of ['detail', 'non_field_errors']) {
      const message = firstErrorMessage(data[key]);
      if (message) return message;
    }
    for (const item of Object.values(data)) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  return null;
}

const wrapError =
  (action: string) =>
  (err: AxiosError): never => {
    const serverMessage = firstErrorMessage(err.response?.data);
    if (serverMessage) throw new Error(serverMessage);
    throw new Error(`${action}에 실패하였습니다. 관리자에게 문의 주세요.`);
  };

/** API 명세: Slot[] / 구형: id[] */
function normalizeBatchSlotSideEffectList(raw: unknown): BatchSlotSideEffectList {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (typeof raw[0] === "number") return raw as number[];
  return raw as Slot[];
}

function normalizeSlotsResponse(data: unknown): GetSlotsResponse {
  if (data && typeof data === "object") {
    const source = data as Partial<GetSlotsResponse> & {
      results?: Slot[];
      count?: number;
      page?: number;
      page_size?: number;
    };
    const slots = Array.isArray(source.slots)
      ? source.slots
      : Array.isArray(source.results)
        ? source.results
        : [];
    const pageSize = source.pagination?.page_size ?? source.page_size ?? 20;
    const totalCount = source.pagination?.total_count ?? source.count ?? slots.length;

    return {
      slots,
      pagination: source.pagination ?? {
        current_page: source.page ?? 1,
        page_size: pageSize,
        total_count: totalCount,
        total_pages: Math.max(1, Math.ceil(totalCount / pageSize)),
        has_next: false,
        has_previous: false,
        next_page: null,
        previous_page: null,
      },
      filters: source.filters ?? {
        slotId: null,
        sellerId: null,
        creatorId: null,
        keyword: null,
        url: null,
        mid_single: null,
        mid_compare: null,
        active: "all",
      },
    };
  }

  return {
    slots: [],
    pagination: {
      current_page: 1,
      page_size: 20,
      total_count: 0,
      total_pages: 1,
      has_next: false,
      has_previous: false,
      next_page: null,
      previous_page: null,
    },
    filters: {
      slotId: null,
      sellerId: null,
      creatorId: null,
      keyword: null,
      url: null,
      mid_single: null,
      mid_compare: null,
      active: "all",
    },
  };
}

function normalizeAllSlotsResponse(data: unknown): GetAllSlotsResponse {
  if (data && typeof data === "object") {
    const source = data as Partial<GetAllSlotsResponse> & {
      results?: Slot[];
      count?: number;
    };
    const slots = Array.isArray(source.slots)
      ? source.slots
      : Array.isArray(source.results)
        ? source.results
        : [];

    return {
      slots,
      total_count: source.total_count ?? source.count ?? slots.length,
    };
  }

  return { slots: [], total_count: 0 };
}

export class SlotRepositoryImpl implements SlotRepository {
  /** 공용 axios 인스턴스 */
  private http: AxiosInstance;

  constructor() {
    this.http = api;
  }

  /* ----------------- CRUD ----------------- */
  createSlot(dto: CreateSlotDTO): Promise<Slot> {
    return this.http
      .post<Slot>("/slots/", dto)
      .then((r) => r.data)
      .catch(wrapError("슬롯 생성"));
  }

  batchCreate(request: { count: number; slots: CreateSlotDTO[] }): Promise<Slot[]> {
    return this.http
      .post<Slot[] | { created_slots?: Slot[] }>("/slots/batch_create/", request)
      .then((r) => {
        const data = r.data;
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.created_slots)) return data.created_slots;
        return [];
      })
      .catch(wrapError("슬롯 일괄 생성"));
  }

  updateSlot(id: number, dto: Partial<Slot>) {
    return this.http
      .patch<Slot>(`/slots/${id}/`, dto)
      .then((r) => r.data)
      .catch(wrapError("슬롯 수정"));
  }

  deleteSlot(id: number) {
    return this.http
      .delete(`/slots/${id}/`)
      .then(() => {})
      .catch(wrapError("슬롯 삭제"));
  }

  /* ----------------- 상태 변경 ----------------- */
  extendSlot(id: number, days: number) {
    return this.http
      .patch<Slot>(`/slots/${id}/extend/`, { days })
      .then((r) => r.data)
      .catch(wrapError("슬롯 연장"));
  }

  refundSlot(id: number) {
    return this.http
      .patch<Slot>(`/slots/${id}/refund/`)
      .then((r) => r.data)
      .catch(wrapError("슬롯 환불"));
  }

  /* ----------------- 배치 처리 ----------------- */
  batchExtend(ids: number[], days: number): Promise<BatchSlotSideEffectList> {
    return this.http
      .post<{ extended_slots: unknown }>("/slots/batch_extend/", {
        slot_ids: ids,
        days,
      })
      .then((r) => normalizeBatchSlotSideEffectList(r.data.extended_slots))
      .catch(wrapError("일괄 연장"));
  }

  batchRefund(ids: number[]): Promise<BatchSlotSideEffectList> {
    return this.http
      .post<{ refunded_slots: unknown }>("/slots/batch_refund/", {
        slot_ids: ids,
      })
      .then((r) => normalizeBatchSlotSideEffectList(r.data.refunded_slots))
      .catch(wrapError("일괄 환불"));
  }

  batchUpdate(slots: Partial<Slot>[]): Promise<BatchUpdateResponse> {
    // API 명세에 맞춰 slotId를 id로 변환하여 전송
    const mappedSlots = slots.map(({ slotId, ...rest }) => ({
      id: slotId,
      ...rest,
    }));

    return this.http
      .post<BatchUpdateResponse>("/slots/batch_update/", { slots: mappedSlots })
      .then((r) => r.data)
      .catch(wrapError("슬롯 일괄 수정"));
  }

  checkExists(ids: number[]): Promise<CheckExistsResponse> {
    return this.http
      .post<CheckExistsResponse>("/slots/check_exists/", {
        slot_ids: ids,
      })
      .then((r) => r.data)
      .catch(wrapError("슬롯 존재 여부 확인"));
  }

  getBatchDetail(slotIds: number[]): Promise<BatchDetailResponse> {
    return this.http
      .get<BatchDetailResponse>("/slots/batch_detail/", {
        params: { slot_ids: slotIds.join(",") },
      })
      .then((r) => ({
        slots: Array.isArray(r.data?.slots) ? r.data.slots : [],
        missing_ids: Array.isArray(r.data?.missing_ids) ? r.data.missing_ids : [],
      }))
      .catch(wrapError("슬롯 상세 일괄 조회"));
  }

  /* ----------------- 새로운 조회 API ----------------- */
  getSlots(params?: SlotSearchParams): Promise<GetSlotsResponse> {
    return this.http
      .get<unknown>("/slots/", { params })
      .then((r) => normalizeSlotsResponse(r.data))
      .catch(wrapError("슬롯 조회"));
  }

  getPaginationInfo(params?: SlotSearchParams): Promise<GetPaginationInfoResponse> {
    return this.http
      .get<GetPaginationInfoResponse>("/slots/pagination_info/", { params })
      .then((r) => r.data)
      .catch(wrapError("페이지네이션 정보 조회"));
  }

  getAllSlots(me: User, active?: boolean): Promise<GetAllSlotsResponse> {
    const params: any = {};
    if (active !== undefined) {
      params.active = active;
    }
    
    return this.http
      .get<unknown>("/slots/all/", { params })
      .then((r) => normalizeAllSlotsResponse(r.data))
      .catch(wrapError("전체 슬롯 조회"));
  }

  getSlotRankHistory(id: number): Promise<SlotRankHistoryMap | unknown> {
    return this.http
      .get(`/slots/${id}/rank_history/`)
      .then((r) => r.data)
      .catch(wrapError("슬롯 순위 이력 조회"));
  }

  /* ----------------- 기존 조회 API (하위 호환성) ----------------- */
  getSlotsLegacy(params?: { user?: number }): Promise<Slot[]> {
    return this.http
      .get<Slot[]>("/slots/", { params })
      .then((r) => r.data)
      .catch(wrapError("슬롯 조회"));
  }

  getSummary() {
    return this.http
      .get<SlotSummary>("/slots/summary/")
      .then((r) => r.data)
      .catch(wrapError("슬롯 통계 조회"));
  }

  /* ----------------- 엑셀 ----------------- */

  uploadExcel(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http
      .post("/slots/excel/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(() => {})
      .catch(wrapError("엑셀 업로드"));
  }
}
